<template>
  <div class="captcha-container" v-if="!verified">
    <div class="captcha-header">
      <i class="fas fa-shield-alt"></i>
      <span>Human Verification</span>
    </div>
    
    <div class="captcha-challenge">
      <div class="math-problem">
        <span class="number">{{ num1 }}</span>
        <span class="operator">+</span>
        <span class="number">{{ num2 }}</span>
        <span class="equals">=</span>
        <input 
          v-model="userAnswer"
          type="number"
          class="answer-input"
          :class="{ 'error': hasError }"
          placeholder="?"
          @input="handleInput"
          @keyup.enter="checkAnswer"
          min="0"
          max="99"
        />
      </div>
      
      <div class="captcha-actions">
        <button 
          type="button" 
          @click="regenerate" 
          class="refresh-btn"
          :title="$t('captcha.refresh') || 'Generate new problem'"
        >
          <i class="fas fa-redo-alt"></i>
        </button>
        
        <button 
          type="button"
          @click="checkAnswer"
          :disabled="!userAnswer || loading"
          class="verify-btn"
          :class="{ 'correct': isCorrect && !hasError }"
        >
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else-if="isCorrect && !hasError" class="fas fa-check"></i>
          <i v-else class="fas fa-arrow-right"></i>
        </button>
      </div>
    </div>
    
    <div v-if="hasError" class="error-message">
      <i class="fas fa-times-circle"></i>
      {{ $t('captcha.incorrect') || 'Incorrect answer. Please try again.' }}
    </div>
    
    <div v-if="isCorrect && !hasError" class="success-message">
      <i class="fas fa-check-circle"></i>
      {{ $t('captcha.correct') || 'Verification successful!' }}
    </div>
  </div>
  
  <div v-else class="captcha-verified">
    <i class="fas fa-check-circle"></i>
    <span>{{ $t('captcha.verified') || 'Human verification completed' }}</span>
  </div>
</template>

<script>
export default {
  name: 'Captcha',
  emits: ['verified', 'failed'],
  data() {
    return {
      num1: 0,
      num2: 0,
      userAnswer: '',
      verified: false,
      hasError: false,
      loading: false,
      attempts: 0,
      maxAttempts: 3
    }
  },
  computed: {
    correctAnswer() {
      return this.num1 + this.num2
    },
    isCorrect() {
      return parseInt(this.userAnswer) === this.correctAnswer
    }
  },
  methods: {
    generateProblem() {
      // Generate numbers that result in answers between 10-30 for reasonable difficulty
      this.num1 = Math.floor(Math.random() * 15) + 5  // 5-19
      this.num2 = Math.floor(Math.random() * 15) + 5  // 5-19
    },
    
    handleInput() {
      this.hasError = false
      // Auto-check when user types a reasonable answer
      if (this.userAnswer.length >= 2 && this.isCorrect) {
        setTimeout(() => {
          this.checkAnswer()
        }, 500)
      }
    },
    
    async checkAnswer() {
      if (!this.userAnswer) return
      
      this.loading = true
      
      // Add small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300))
      
      if (this.isCorrect) {
        this.hasError = false
        this.verified = true
        
        // Show success state briefly before emitting
        setTimeout(() => {
          this.$emit('verified', {
            challenge: `${this.num1}+${this.num2}=${this.correctAnswer}`,
            answer: this.userAnswer,
            timestamp: new Date().toISOString()
          })
        }, 800)
      } else {
        this.hasError = true
        this.attempts++
        
        if (this.attempts >= this.maxAttempts) {
          this.$emit('failed', {
            reason: 'max_attempts_exceeded',
            attempts: this.attempts
          })
          return
        }
        
        // Generate new problem after error
        setTimeout(() => {
          this.regenerate()
        }, 1000)
      }
      
      this.loading = false
    },
    
    regenerate() {
      this.generateProblem()
      this.userAnswer = ''
      this.hasError = false
      this.loading = false
    },
    
    reset() {
      this.verified = false
      this.attempts = 0
      this.regenerate()
    }
  },
  mounted() {
    this.generateProblem()
  }
}
</script>

<style scoped>
.captcha-container {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  margin: 16px 0;
  transition: all 0.3s ease;
}

.captcha-container:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.captcha-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-weight: 600;
  color: #475569;
  font-size: 0.9rem;
}

.captcha-header i {
  color: #3b82f6;
}

.captcha-challenge {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.math-problem {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
}

.number {
  background: #3b82f6;
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  min-width: 40px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

.operator,
.equals {
  color: #64748b;
}

.answer-input {
  width: 80px;
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  background: white;
  transition: all 0.2s ease;
}

.answer-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.answer-input.error {
  border-color: #ef4444;
  background-color: #fef2f2;
}

.captcha-actions {
  display: flex;
  gap: 8px;
}

.refresh-btn,
.verify-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.refresh-btn {
  background: #f1f5f9;
  color: #64748b;
}

.refresh-btn:hover {
  background: #e2e8f0;
  color: #475569;
}

.verify-btn {
  background: #3b82f6;
  color: white;
}

.verify-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.verify-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.verify-btn.correct {
  background: #22c55e;
}

.error-message,
.success-message {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.success-message {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.captcha-verified {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  font-weight: 500;
  margin: 16px 0;
}

.captcha-verified i {
  font-size: 1.1rem;
}

/* Animations */
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}

.answer-input.error {
  animation: shake 0.3s ease-in-out;
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .math-problem {
    font-size: 1.2rem;
    gap: 8px;
  }
  
  .number {
    padding: 6px 10px;
    min-width: 35px;
  }
  
  .answer-input {
    width: 60px;
    font-size: 1rem;
  }
  
  .refresh-btn,
  .verify-btn {
    width: 35px;
    height: 35px;
  }
}
</style>